import api from '../../services/api'

/**
 * One financing offer for one car variant, as of one control date (#583).
 *
 * The numeric fields arrive as strings because the backend serialises MySQL
 * DECIMAL through Pydantic — same as the leasing endpoint. `null` means the
 * source did not publish the figure; `"0.000"` means the figure is zero, and
 * 21 of 127 rows carry a genuine 0 % campaign rate. The two must not be
 * collapsed, so every accessor here checks `== null` rather than falsiness.
 *
 * The percentage fields are in percent units: `NominellRentePct: "0.990"` is
 * 0.99 %, not 99 %. This differs from the leasing endpoint's `InterestRate`,
 * which is a fraction.
 */
export interface LoanCampaign {
  ResultID: number
  KontrollDato: string

  MerkeRaw: string
  Modell: string | null
  Variant: string | null
  BrandCode: string | null

  FinansieringsselskapRaw: string | null
  BankCode: string | null

  BilprisNOK: string | null
  NominellRentePct: string | null
  EffektivRentePct: string | null
  RentebindingMnd: number | null
  EgenkapitalNOK: string | null
  EgenkapitalPct: string | null
  LanebelopNOK: string | null
  LopetidMnd: number | null
  ManedsbelopNOK: string | null
  TotalkostnadNOK: string | null
  EtableringsgebyrNOK: string | null
  TermingebyrNOK: string | null
  ProduktrenteEtterKampanjePct: string | null
  KontantrabattNOK: string | null

  /** Derived by the backend from Merknad (#609): "Flytende", "Fast" or null
   *  when the note names neither — 64 of 130 rows in the 2026-09-17 batch. */
  lanetype_p1: string | null

  Kampanjenavn: string | null
  InkludertUtstyr: string | null
  KampanjeFra: string | null
  KampanjeTil: string | null
  Status: string | null

  Merknad: string | null
  EndringFraForrigeKontroll: string | null
  KildeUrl: string | null
  KampanjeUrl: string | null
  KildeSistKontrollert: string | null
}

export interface LoanCampaignList {
  data: LoanCampaign[]
  total: number
  /** Control date of the returned batch; null when no batch has been uploaded. */
  kontroll_dato: string | null
}

/**
 * Fetch the newest active snapshot. The batch is ~130 rows, so the whole thing
 * is pulled once and filtered client-side — there is no filter-driven refetch
 * to guard against.
 */
export async function fetchLoanCampaigns(): Promise<LoanCampaignList> {
  const response = await api.get('/public/loan-campaigns/', { params: { skip: 0, limit: 1000 } })
  return {
    data: response.data?.data ?? [],
    total: response.data?.total ?? 0,
    kontroll_dato: response.data?.kontroll_dato ?? null,
  }
}
